/**
 * A component that safely renders and executes HTML content with inline scripts.
 *
 * Use this component when you need to:
 * - Dynamically insert HTML content that includes script tags
 * - Execute script content in a controlled manner
 * - Handle content updates without accumulating event listeners
 *
 * @remarks
 * This component uses createContextualFragment() to properly parse and execute
 * script tags, unlike dangerouslySetInnerHTML which only renders HTML.
 * It also includes safeguards for React Strict Mode to prevent double execution.
 *
 * @param props
 * @param props.html - Raw HTML string that may include script tags to be executed
 *
 * @example
 * ```tsx
 * <LiveFragment html="<div>Content <script>console.log('Hello');</script></div>" />
 * ```
 *
 * @security
 * Be careful when using this component as it executes arbitrary script content.
 * Ensure the HTML source is trusted to prevent XSS attacks.
 */
import { useRef, useLayoutEffect } from "react";

const LiveFragment = ({ html }: { html: string }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasExecutedRef = useRef<boolean>(false); // Handle React Strict Mode

  useLayoutEffect(() => {
    // Skip if already executed in Strict Mode or no container
    if (!containerRef.current || hasExecutedRef.current) return;

    // Mark as executed for Strict Mode
    hasExecutedRef.current = true;

    try {
      const range = document.createRange();
      range.selectNode(containerRef.current);
      const fragment = range.createContextualFragment(html);
      // Clear existing content and append the new fragment
      containerRef.current.innerHTML = "";
      containerRef.current.append(fragment);
    } catch (error) {
      console.error("Error executing script fragment:", error);
    }
  }, [html]);

  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: html }} />;
};

export default LiveFragment;
